import React, { useState } from "react";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import useApi from "@/hooks/useApi";
import { AgreementInfo } from "@/hooks/useAgreementStatus";

interface Props {
  agreement: AgreementInfo;
  onAccepted: () => void;
}

export default function LegalAgreementModal({ agreement, onAccepted }: Props) {
  const { displayToast } = useToastAlert();
  const api = useApi();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      const resp = await api.messaging.post(`LegalAgreements/${agreement.slug}/accept`, {});
      if (resp.ok) {
        onAccepted();
      } else {
        displayToast(Severity.Error, "Failed to record your acceptance. Please try again.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Full-screen blocking overlay — no close button, cannot be dismissed without accepting
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-agreement-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-0.5">
              Velaro Platform
            </p>
            <h2 id="legal-agreement-title" className="text-lg font-semibold text-slate-800">
              {agreement.title}
            </h2>
          </div>
          <span className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-1 rounded">
            v{agreement.policyVersion}
          </span>
        </div>

        {/* Scrollable content — user must scroll to read */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {/*
            HTML content is authored exclusively by Velaro SuperAdmins via the
            SuperAdmin panel — not end-user input. Rendering as innerHTML is
            appropriate here. The messaging backend further validates via HtmlSanitizer.
          */}
          <div
            className="prose prose-sm max-w-none text-slate-700
              [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-800 [&_h2]:mt-4 [&_h2]:mb-2
              [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-700 [&_h3]:mt-4 [&_h3]:mb-2
              [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3
              [&_ul]:text-sm [&_ul]:pl-5 [&_ul]:mb-3 [&_ul>li]:mb-1
              [&_strong]:font-semibold"
            // Trusted admin-authored content — no user-supplied HTML
            dangerouslySetInnerHTML={{ __html: agreement.htmlContent }}
          />
        </div>

        {/* Accept section — always visible below scroll area */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl shrink-0">
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300"
              aria-label="I understand and accept this agreement"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              I understand and accept this agreement on behalf of my organization, and confirm I am authorized to bind my company to its terms.
            </span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!checked || submitting}
            className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-all
              ${checked && !submitting
                ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            aria-disabled={!checked || submitting}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />
                Processing...
              </span>
            ) : (
              "I Accept & Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
